import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Grid,
  Paper,
} from '@mui/material';
import {
  Email,
  Business,
  Person,
  Verified,
  Group,
  Description,
} from '@mui/icons-material';

const ApprovalStamp = ({ approvalInfo, invoice }) => {
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  
  if (!approvalInfo) {
    return null;
  }

  const approvalDate = new Date(approvalInfo.date_confirmed);
  const daysSinceApproval = Math.floor((new Date() - approvalDate) / (1000 * 60 * 60 * 24));

  // Extract enhanced approval information
  const approverName = approvalInfo.approved_by_name || 'Approver';
  const approverEmail = approvalInfo.approved_by_email || 'N/A';
  const companyName = approvalInfo.company_name || approvalInfo.company?.name || 'Unknown Company';
  const companyLogo = approvalInfo.company_logo || approvalInfo.company?.phphref;
  const teamInfo = approvalInfo.team_info || approvalInfo.team;
  const approverRole = approvalInfo.approver_role || 'Approver';
  const approverDepartment = approvalInfo.department || 'N/A';

  return (
    <Card 
      sx={{ 
        mb: 3, 
        borderRadius: 3,
        border: '3px solid',
        borderColor: 'success.main',
        position: 'relative',
        overflow: 'visible',
        boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)'
      }}
    >
      {/* Approval Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -12,
          right: 16,
          bgcolor: 'success.main',
          color: 'white',
          px: 2.5,
          py: 0.75,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: 3
        }}
      >
        <Verified sx={{ fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700}>
          APPROVED
        </Typography>
      </Box>

      <CardContent sx={{ p: 3, pt: 4 }}>
        {/* Header with Enhanced User Info */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'success.main', 
                width: 56, 
                height: 56,
                fontSize: '1.35rem',
                fontWeight: 700
              }}
            >
              {approverName.split(' ').map(n => n[0]).join('')}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Approved by {approverName}
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Email sx={{ fontSize: 14 }} />
                  {approverEmail}
                </Typography>
                {approverRole && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: 14 }} />
                    {approverRole}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              {daysSinceApproval === 0 ? 'Today' : `${daysSinceApproval} days ago`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {approvalDate.toLocaleDateString()} at {approvalDate.toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>

        {/* Company and Team Information */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  {companyLogo ? (
                    <Avatar src={companyLogo} sx={{ width: 32, height: 32 }}>
                      <Business />
                    </Avatar>
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      <Business />
                    </Avatar>
                  )}
                  <Typography variant="subtitle2" fontWeight={600} color="primary">
                    Company Information
                  </Typography>
                </Box>
                <Stack spacing={0.75}>
                  <Typography variant="body2">
                    <strong>Company:</strong> {companyName}
                  </Typography>
                  {approverDepartment !== 'N/A' && (
                    <Typography variant="body2">
                      <strong>Department:</strong> {approverDepartment}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {teamInfo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      <Group />
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={600} color="secondary">
                      Team Information
                    </Typography>
                  </Box>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      <strong>Team:</strong> {teamInfo.team_name || teamInfo.name || 'Unknown Team'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Role:</strong> {teamInfo.role === 'leader' ? 'Team Leader' : 'Team Member'}
                    </Typography>
                    {teamInfo.member_count && (
                      <Typography variant="body2">
                        <strong>Team Size:</strong> {teamInfo.member_count} members
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Approval Reasoning */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description color="warning" sx={{ fontSize: 18 }} />
            Approval Reasoning
          </Typography>
          <Paper sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {approvalInfo.reasoning || approvalInfo.Reasoning || 'Invoice has been approved for payment.'}
            </Typography>
          </Paper>
        </Box>

        {/* Additional Approval Details */}
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Approval Method
            </Typography>
            <Chip 
              label="Digital Approval"
              color="success"
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Approval Authority
            </Typography>
            <Chip 
              label={teamInfo?.role === 'leader' ? 'Team Leader' : 'Authorized User'}
              color="info"
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Verification Status
            </Typography>
            <Chip 
              label="Verified"
              color="success"
              size="small"
              icon={<Verified />}
            />
          </Grid>
        </Grid>

        {/* Approval Metadata */}
        {(approvalInfo.approval_amount || approvalInfo.credit_impact) && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Approval Details
            </Typography>
            <Grid container spacing={2}>
              {approvalInfo.approval_amount && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Approved Amount:</strong> ${parseFloat(approvalInfo.approval_amount).toFixed(2)}
                  </Typography>
                </Grid>
              )}
              {approvalInfo.credit_impact && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Spending Power Impact:</strong> ${parseFloat(approvalInfo.credit_impact).toFixed(2)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovalStamp;